<template>
  <article
    :style="accentStyle"
    :aria-label="image.name"
    :aria-describedby="detailsId"
    :class="{
      'image-result': true,
      'image-result--expanded': previewOpen,
    }"
    @keypress.enter.space.prevent="$emit('preview-toggle')"
  >
    <div class="image-result__link-wrapper">
      <a
        :href="image.contentURL"
        rel="noopener"
        :target="linkTarget"
        class="image-result__link"
        data-test-id="image-result-link"
        :aria-expanded="previewOpen ? 'true' : 'false'"
        :aria-controls="previewId"
        @click.prevent="$emit('preview-toggle')"
      >
        <img
          :src="lazyLoad ? lazyUrl : image.thumbnailURL"
          :data-src="image.thumbnailURL"
          :height="image.thumbnail.height"
          :width="image.thumbnail.width"
          :alt="image.name"
          class="image-result__image"
        >
        <ELabel
          v-if="labelShown"
          variant="solid-dark"
          class="image-result__label"
          data-test-id="image-result-label"
        >
          <template v-if="hasVideoSource">
            <EIcon class="image-result__video-icon" icon="play" size="s" />
            Video
          </template>
          <template v-else>
            GIF
          </template>
        </ELabel>
        <div
          class="image-result__dimensions"
          data-test-id="image-result-dimensions"
        >
          {{ dimensions }}
        </div>
      </a>
    </div>
    <div :id="detailsId" class="image-result__details">
      <a
        :href="image.hostPageURL"
        :target="linkTarget"
        rel="noopener"
        data-track-id="images-thumbnail-title-link"
        class="image-result__details-link"
      >
        <h2 class="image-result__details-title">

          {{ image.name }}
        </h2>
        <div
          class="image-result__details-source"
          data-test-id="image-result-source"
        >
          {{ source }}
        </div>
      </a>
    </div>
    <EImagePreview
      v-if="previewOpen"
      :id="previewId"
      class="image-result__preview"
      :image="image"
      :is-first="isFirst"
      :is-last="isLast"
      @close="onPreviewClose"
      @previous="$emit('preview-previous')"
      @next="$emit('preview-next')"
    />
  </article>
</template>

<script>
import { mapGetters } from 'vuex';

import { isHexColor } from '~~/common/js/universal/validators';
import EIcon from '~~/common/vue/components/icon';
import ELabel from '~~/common/vue/components/label';

import { getImageSource, getImageDimensions, hasVideoSource } from '~/utils/image-data';
import EImagePreview from './image-preview';

export default {
  name: 'EImageResult',
  components: {
    EIcon,
    EImagePreview,
    ELabel,
  },
  props: {
    image: {
      type: Object,
      required: true,
    },
    lazyLoad: {
      type: Boolean,
      default: false,
    },
    previewOpen: {
      type: Boolean,
      default: false,
    },
    isFirst: {
      type: Boolean,
      default: false,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    lazyUrl: '',
  }),
  computed: {
    ...mapGetters('user', ['linkTarget']),
    accentStyle() {
      const { image: { accentColor } } = this;
      const color = isHexColor(accentColor) ? accentColor : 'fff';
      return `--image-result-accent: #${color};`;
    },
    source() {
      return getImageSource(this.image);
    },
    detailsId() {
      return `image-details-${this.image.imageID}`;
    },
    previewId() {
      return `image-preview-${this.image.imageID}`;
    },
    dimensions() {
      return getImageDimensions(this.image);
    },
    isAnimatedGif() {
      return this.image.encodingFormat === 'animatedgif';
    },
    hasVideoSource() {
      return hasVideoSource(this.image);
    },
    labelShown() {
      return this.hasVideoSource || this.isAnimatedGif;
    },
  },
  watch: {
    async previewOpen(isOpen) {
      if (isOpen) {
        await this.$nextTick();
        this.$el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    },
  },
  mounted() {
    this.lazyUrl = this.image.thumbnailURL;
  },
  methods: {
    onPreviewClose() {
      this.$emit('preview-close');
      this.$el.focus();
    },
  },
};
</script>

<style lang="scss" scoped>
// stylelint-disable no-extra-semicolons
// TODO: figure out why the hell stylelint complains about extra semicolons here
$preview-desktop-size: 440px;

.image-result__dimensions {
  background: $gray-60;
  bottom: 0;
  color: $white;
  font-size: $font-1s;
  line-height: 1;
  padding: $space-2s;
  position: absolute;
  right: 0;
  transform: translateY(100%);
  transition: transform $timing-2s;
}

.image-result__link-wrapper {
  transition: box-shadow $easing;
}

.image-result--expanded,
.image-result:hover,
.image-result:focus {
  .image-result__link-wrapper {
    @include elevation(2);
  }

  .image-result__dimensions {
    transform: translateY(0);
  }
}

.image-result__link {
  display: block;
  position: relative;
  overflow: hidden;
}

.image-result__image {
  background-color: var(--image-result-accent);
  // the alt text shouldn't be visually shown
  color: $transparent;
  height: auto;
  width: 100%;
}

.image-result__details {
  padding: $space-1s 0 0;
  position: relative;
  width: 100%;

  &::after {
    // explicit pixel values since this is a custom arrow design
    $height: 7px;
    $side-size: 8px;

    opacity: 0;
    bottom: -$space-s;
    left: calc(50% - #{$space-1s});
    width: 0;
    height: 0;
    border-right: $side-size solid $transparent;
    border-bottom: $height solid $gray-70;
    border-left: $side-size solid $transparent;
    content: "";
    position: absolute;
    transform: translateY(100%);
    transition: transform $timing-s $easing;
  }
}

.image-result__details-title {
  @extend %ellipsis;

  font-size: $font-s;
  font-weight: normal;
  margin: 0;

  @include desktop {
    font-size: $font-m;
  }
}

.image-result__details-source {
  @extend %ellipsis;

  color: $gray-40;
  font-size: $font-1s;
}

.image-result__details-link {
  display: block;

  &:active,
  &:focus,
  &:hover {
    .image-result__details-title {
      text-decoration: underline;
      // Chrome is rendering weird thick red/black underlines sometimes,
      // could not find any info online as of 15/09/2020
      // TODO: figure out why and remove the line below when not needed
      text-decoration-skip-ink: none;
    }
  }
}

.image-result__preview {
  height: 100%;
  left: 0;
  margin: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: $z-index-1l;

  @include desktop {
    height: $preview-desktop-size - $space-m;
    margin: $space-s 0 0;
    position: absolute;
    top: auto;
    width: 100%;
  }
}

.image-result__label {
  position: absolute;
  bottom: $space-1s;
  left: $space-1s;
}

.image-result__video-icon {
  margin-left: -$space-1s;
}

.image-result.image-result--expanded {
  .image-result__details::after {
    opacity: 1;
    transform: translateY(0);
  }

  @include desktop {
    padding-bottom: $preview-desktop-size;
  }
}
</style>
